import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import {
  Button,
  TableColumn,
  PagingQuery,
} from 'tea-component';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import TableImg from '@/pages/intelligence/components/TableComponents/components/TableImg';
import ColumnsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';

import SvgIcon from '@/pages/home/components/SvgIcon';
import {
  GenreHeader,
  GenreRender,
} from '@/pages/intelligence/components/TableComponents/components/TableGenre';
import type { IGameType } from '@/pages/intelligence/types/common';
import type { TopchartTableProps } from '@/pages/intelligence/pages/_config/topchartsInterface';
import {
  ITopChartsCompaniesBaseProps,
  ITopChartsCompaniesRank,
} from '@/pages/intelligence/pages/TopCharts/Companies/components/CompaniesCore/types';
import { iconPlatformType } from '../../../utils';
import { formattingPopupDatafn, streamingGetIconJumpPayload, streamingGetPictureJumpPayload } from '../../../../Games/utils';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { CompaniesMetricColumn, COLUMNS_METADATAS_MAPPING } from './utils';

const DEFAULT_PAGE_SIZE = 100;

interface ICompaniesTablesProps
  extends TopchartTableProps,
  ITopChartsCompaniesBaseProps {
  gameTypes?: IGameType[];
  imgActive?: string;
  onTablGoToDetails: (...argds: any[]) => void;
  getDetailGamePath: (...argds: any[]) => string;
}

function StreamingTables(props: ICompaniesTablesProps) {
  const {
    loading,
    topChartsType,
    gameTypes = [
      'hoursWatched',
      'peackCCV',
      'avgCCV',
      'airtime',
    ] as IGameType[],
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onTablGoToDetails,
    getDetailGamePath,
    imgActive,
  } = props;
  const { formatMessage } = useIntl();
  const {
    tablePageIndex,
    tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
  } = pageSortAttr || {};
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || DEFAULT_PAGE_SIZE);
  const [selectList, setSelectList] = useState(tableSelection);

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [, tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || DEFAULT_PAGE_SIZE);
    onTablePagingChange(e);
  };

  const onSortChage = useCallback(
    (e: SortByItemType) => {
      onTableSortChage(e);
    },
    [onTableSortChage],
  );
  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricCloums =
    useCallback((): TableColumn<ITopChartsCompaniesRank>[] => {
      const metricCloums: any[] = [];
      let titles: any[] = [];
      if (topChartsType) {
        // 追加revenue的动态列配置，根据勾选的gameType不同
        const keys = {
          hoursWatched: 'Hours_Watched',
          peackCCV: 'Peak_CCV',
          avgCCV: 'Avg_CCV',
          airtime: 'Airtime',
        } as const;
        const revenueTitles =
          gameTypes?.map((gameType) => {
            const key = keys[gameType];
            return COLUMNS_METADATAS_MAPPING[key];
          }) || [];
        titles = [...titles, ...revenueTitles];
      }
      titles.forEach((item: any) => {
        const metricKey = item?.key;
        metricCloums.push(
          CompaniesMetricColumn({
            topChartsType,
            dataSources: item?.dataSources,
            dataSource: item?.dataSource,
            key: metricKey,
            title: item?.title,
            title_cn: item?.title_cn,
            content: item?.content,
            tipText: item?.tipText,
            sortMetric: metricKey,
            currentSortKey: `${currentSortMetric}_${currentRatioType}`,
            currentSortValue,
            onSortChage,
          }),
        );
      });
      return metricCloums;
    }, [
      currentRatioType,
      currentSortMetric,
      currentSortValue,
      gameTypes,
      onSortChage,
      topChartsType,
    ]);

  const handleClickCompany = (url: string) => {
    window.open(url);
  };

  const commonTableConfig = useMemo(() => {
    const fileds = {
      streaming_kol_revenue: [
        {
          key: 'kol',
          fixed: 'left',
          width: 330,
          header: (
            <div className="dbi-companies-table-header-other dbi-firstRow-flex">
              <span className="dbi-companies-table-flex-id dbi-text-id">#</span>
              {/* <Bubble style={{ maxWidth: 500 }} content={<CBSvgIconTips />}>
              <img className="dbi-companies-table-header-icon" src={Hatchet} />
            </Bubble> */}
              <span>{formatMessage({ id: 'dbi_Primary_kol' })}</span>
            </div>
          ),
          render: (cvm) => {
            const { icon, name } = cvm?.user_info;
            const userCvm = cvm?.user_info;
            const descript = userCvm?.platform === 'twitch' ? userCvm?.language : userCvm?.platform === 'ytg' ? userCvm?.market : '';
            const countryIcon = userCvm?.platform === 'ytg' ? getCountryFlag(userCvm?.market_abbre) : undefined;

            return (
              <FirstRow
                id={cvm?.rank}
                cvm={{
                  gametitle: name,
                  img: icon,
                  countryIcon,
                  text: descript,
                }}
                showImg={false}
                showUserImg={true}
                rihgtClickToJumpHref={cvm?.user_info?.url}
                onClick={() => {
                  handleClickCompany(cvm?.user_info?.url);
                }}
              />
            );
          },
        },
        {
          key: 'platform',
          width: 150,
          align: 'left',
          header: (
            <ColumnsCenter width={105} align="left">
              <div className="dbi-companies-table-header-end">
                {formatMessage({ id: 'dbi_platform' })}
              </div>
            </ColumnsCenter>
          ),
          render: (cvm) => {
            const { name, imgUrl } = iconPlatformType(cvm?.user_info.platform);
            return (
              <ColumnsCenter width={150} align="right">
                <div style={{ display: 'flex', alignContent: 'center' }}>
                  <img src={imgUrl as string} className="kol-card-img" />
                  &nbsp;&nbsp;{name}
                </div>
              </ColumnsCenter>
            );
          },
        },
        ...getMetricCloums(),
        {
          key: 'primaryGames',
          width: 220,
          align: 'left',
          header: (
            <ColumnsCenter width={150} align="left">
              <div className="dbi-companies-table-header-end">
                {formatMessage({ id: 'dbi_Primary_games' })}
              </div>
            </ColumnsCenter>
          ),
          render: (cvm, index) => (
            <ColumnsCenter width={200} align="left">
              <div style={{ display: 'flex', alignContent: 'center' }}>
                <TableImg
                  cvm={cvm?.games || []}
                  key={index}
                  searchValue={imgActive}
                />
              </div>
            </ColumnsCenter>
          ),
        },
      ],
      streaming_games_revenue: [
        {
          key: 'game',
          fixed: 'left',
          width: 330,
          header: (
            <div className="dbi-companies-table-header-other dbi-firstRow-flex">
              <span className="dbi-companies-table-flex-id dbi-text-id">#</span>
              {/* <Bubble style={{ maxWidth: 500 }} content={<CBSvgIconTips />}>
              <img className="dbi-companies-table-header-icon" src={Hatchet} />
            </Bubble> */}
              <span>{formatMessage({ id: 'dbi_game' })}</span>
            </div>
          ),
          render: (cvm) => {
            const { cover, name, publisher_address, publisher } = cvm || {};
            const iconList = formattingPopupDatafn(
              cvm?.game_type,
            );
            return (
              <FirstRow
                id={cvm?.rank}
                cvm={{
                  gametitle: name,
                  img: cover,
                  countryIcon: getCountryFlag(publisher_address),
                  text: publisher,
                  customIcon: iconList?.length ? (
                    <>
                      {iconList?.map((t: any, idx: number) => (
                        <a
                          className="benchmark-table-plat-alink"
                          key={t?.value || idx}
                          target="_blank"
                          href={t?.entityType ? getDetailGamePath(streamingGetIconJumpPayload(cvm?.game_type, t?.entityType)) : ''}
                          onClick={(e) => {
                            e.stopPropagation();
                          }} rel="noreferrer"
                        >
                          <SvgIcon
                            className="benchmark-table-gametype-icon"
                            type={t?.icon}
                          />
                        </a>
                      ))}
                      <span className="benchmark-table-region-divide"></span>
                    </>
                  ) : null,
                }}
                rihgtClickToJumpHref={getDetailGamePath(streamingGetPictureJumpPayload(cvm?.game_type))}
                onClick={() => {
                  onTablGoToDetails(streamingGetPictureJumpPayload(cvm?.game_type));
                }}
              />
            );
          },
        },
        ...getMetricCloums(),
        {
          key: 'primaryChannel',
          width: 250,
          align: 'left',
          header: (
            <ColumnsCenter width={160} align="left">
              <div className="dbi-companies-table-header-end">
                {formatMessage({ id: 'dbi_Primary_channel' })}
              </div>
            </ColumnsCenter>
          ),
          render: (cvm) => (
            <ColumnsCenter width={300} align="left">
              <div style={{ display: 'flex', alignContent: 'center' }}>
                <TableImg
                  searchValue={imgActive}
                  cvm={cvm?.top_channel || []}
                  getClick={(i) => window.open(i.url)}
                  isNeedMogr={true}
                  entityType={'user'}
                />
              </div>
            </ColumnsCenter>
          ),
        },
        {
          key: 'releaseDate',
          width: 135,
          align: 'center',
          header: (
            <ColumnsCenter width={130.5} align="left">
              <div className="dbi-companies-table-header-end">
                {formatMessage({ id: 'dbi_release_date' })}
              </div>
            </ColumnsCenter>
          ),
          render: (cvm) => (
            <ColumnsCenter width={140} align="left">
              {cvm?.release_time}
            </ColumnsCenter>
          ),
        },
        {
          key: 'genre',
          header: (
            <div className="header-other">
              <GenreHeader />
            </div>
          ),
          width: 220,
          align: 'left',
          render: (cvm: any) => (
            <GenreRender
              mainGenre={cvm?.genre || '-'}
              subGenre={cvm?.sub_genre || '-'}
            />
          ),
        },
      ],
    }[topChartsType as string];
    return fileds || [];
  }, [formatMessage, getMetricCloums, onTablGoToDetails, topChartsType]);
  return (
    <>
      <TopchartTable
        loading={loading}
        recordKey="rank"
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={
          [
            {
              key: 'select',
              fixed: 'left',
              width: 36,
              header: (
                <TableCheckboxHeader
                  disabled={loading || records?.length === 0}
                  tableSelectionAll={tableSelectionAll}
                  selectList={selectList}
                  onChange={(list, stats) => {
                    onTableCheckGame(list, stats);
                    setSelectList(list);
                  }}
                />
              ),
              render: (cvm) => (
                <TableCheckboxRender
                  cvm={{
                    ...(cvm || {}),
                    gameId: cvm?.user_info?.id || cvm?.id,
                    color: '',
                  }}
                  selectList={selectList}
                  onChange={(list, status) => {
                    setSelectList(list);
                    onTableCheckGame(list, status);
                  }}
                />
              ),
            },

            ...commonTableConfig,
            {
              key: 'actions',
              header: formatMessage({ id: 'dbi_actions' }),
              align: 'left',
              width: 100,
              render: (cvm) => (
                <Button
                  className="table-link"
                  type="link"
                  onClick={() => {
                    if (topChartsType === 'streaming_kol_revenue') {
                      handleClickCompany(cvm?.user_info?.url);
                    } else if (topChartsType === 'streaming_games_revenue') {
                      onTablGoToDetails?.(streamingGetPictureJumpPayload(cvm?.game_type));
                    }
                  }}
                >
                  {formatMessage({ id: 'dbi_details' })}
                </Button>
              ),
            },
          ] as TableColumn<ITopChartsCompaniesRank>[]
        }
      />
    </>
  );
}
export default memo(StreamingTables);
